import React, { useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import styles from "./map.module.css";
import { stateLocations, cityCoordinates, headquarters } from "../../CONTENT/locations";

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const stateOf = (city) => Object.keys(stateLocations).find((s) => stateLocations[s].includes(city));
const siteCount = Object.values(stateLocations).reduce((n, cities) => n + cities.length, 0);
const stateCount = Object.keys(stateLocations).length;

export default function USAMap() {
  const [tooltip, setTooltip] = useState(null);

  const show = (city, state, e) =>
    setTooltip({
      text: `${city === headquarters ? "KUR Research Headquarters · " : ""}${city}, ${state}`,
      x: e.clientX,
      y: e.clientY,
    });

  return (
    <div className={styles.container}>
      <div className={styles.mapWrapper}>
        <ComposableMap projection="geoAlbersUsa" className={styles.svg} aria-hidden="true">
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const active = Boolean(stateLocations[geo.properties.name]?.length);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    className={active ? styles.stateActive : styles.state}
                    style={{ default: { outline: "none" }, hover: { outline: "none" }, pressed: { outline: "none" } }}
                  />
                );
              })
            }
          </Geographies>

          {Object.entries(cityCoordinates).map(([city, coords], i) => {
            const state = stateOf(city);
            if (!state) return null;
            const hq = city === headquarters;
            return (
              <Marker
                key={city}
                coordinates={coords}
                onMouseEnter={(e) => show(city, state, e)}
                onMouseMove={(e) => show(city, state, e)}
                onMouseLeave={() => setTooltip(null)}
              >
                <circle
                  r={hq ? 11 : 7}
                  className={styles.pulse}
                  style={{ animationDelay: `${(i % 6) * 0.35}s` }}
                />
                {hq ? (
                  <g className={styles.hq}>
                    <circle r="9" />
                    <path
                      d="M0-5.2l1.5 3.1 3.4.5-2.5 2.4.6 3.4L0 2.6l-3 1.6.6-3.4-2.5-2.4 3.4-.5z"
                      className={styles.hqStar}
                    />
                    <text y="22" textAnchor="middle" className={styles.hqLabel}>
                      HQ
                    </text>
                  </g>
                ) : (
                  <circle r="4" className={styles.dot} />
                )}
              </Marker>
            );
          })}
        </ComposableMap>
      </div>

      <div className={styles.listHead}>
        <h3 className={styles.locationTitle}>KUR Clinical Research Sites</h3>
        <p className={styles.summary}>
          {siteCount} locations across {stateCount} states
        </p>
      </div>

      <ul className={styles.locationsList}>
        {Object.entries(stateLocations).map(([state, cities]) => (
          <li key={state} className={styles.stateCard}>
            <h4 className={styles.stateName}>{state}</h4>
            <ul className={styles.citiesList}>
              {cities.map((city) => (
                <li key={city} className={styles.locationItem}>
                  {city === headquarters ? (
                    <>
                      <strong>{city}</strong>
                      <span className={styles.hqTag}>HQ</span>
                    </>
                  ) : (
                    city
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>

      {tooltip && (
        <div className={styles.tooltip} style={{ top: tooltip.y, left: tooltip.x }} role="presentation">
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
